<form
  class="user-create-panel"
  data-testid="user-create-panel"
  novalidate
  aria-labelledby="user-access-title"
  [formGroup]="form"
  [attr.aria-busy]="isSaving()"
  (ngSubmit)="submit()"
>
  <header class="user-create-header">
    <span class="user-create-header__avatar" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <circle cx="10" cy="8" r="3.5"></circle>
        <path d="M3.5 19.5c0-3.4 2.9-5.5 6.5-5.5 1.5 0 2.9.4 4 1"></path>
        <path d="M18 14v6M15 17h6"></path>
      </svg>
    </span>
    <div class="user-create-header__identity">
      <h3 id="user-access-title" [appTerminologyIdentity]="usage.title">{{ text.title() }}</h3>
      <span [appTerminologyIdentity]="usage.lead">{{ text.lead() }}</span>
    </div>
    <div class="user-create-header__actions">
      <button
        type="button"
        class="user-create-button user-create-button--secondary"
        data-testid="user-create-cancel"
        [disabled]="isSaving()"
        [appTerminologyIdentity]="usage.cancelAction"
        (click)="cancel()"
      >{{ text.cancelAction() }}</button>
      <button
        type="submit"
        class="user-create-button user-create-button--primary"
        data-testid="user-create-submit"
        [disabled]="isSaving()"
        [appTerminologyIdentity]="isSaving() ? usage.savingState : usage.saveAction"
      >{{ isSaving() ? text.savingState() : text.saveAction() }}</button>
    </div>
  </header>

  <div class="user-create-body">
    @if (summaryError(); as error) {
      <div class="user-create-summary" role="alert" aria-live="assertive" data-testid="user-create-error-summary">
        <p class="user-create-summary__title">{{ error.title }}</p>
        <ul class="user-create-summary__messages">
          @for (message of error.messages; track message) {
            <li>{{ message }}</li>
          }
        </ul>
        @if (error.correlationId) {
          <span class="user-create-summary__meta"><span [appTerminologyIdentity]="usage.correlationIdLabel">{{ text.correlationIdLabel() }}</span>: {{ error.correlationId }}</span>
        }
      </div>
    }

    <p class="user-create-required-note" data-testid="user-create-required-note">
      <span class="user-create-required" aria-hidden="true">*</span>{{ ' ' }}<span [appTerminologyIdentity]="usage.requiredNote">{{ text.requiredNote() }}</span>
    </p>

    <section class="user-create-section" aria-labelledby="user-create-details-title">
      <h4 id="user-create-details-title" [appTerminologyIdentity]="usage.detailsHeading">{{ text.detailsHeading() }}</h4>
      <div class="user-create-fields">
        @for (field of detailFields(); track field.name) {
          <ng-container *ngTemplateOutlet="textField; context: { $implicit: field }"></ng-container>
        }
      </div>
    </section>

    <!--
      The roles section is the Roles tab of a selected user, repeated for a user who does not
      exist yet: filter box, "n of m" count and one checkbox tile per catalog role. Ticking a
      box only records the choice; the API assigns every ticked role when the user is saved.
    -->
    <fieldset
      class="user-create-section user-create-roles"
      aria-required="true"
      [id]="roleGroupId"
      [attr.aria-invalid]="roleMessage() ? true : null"
      [attr.aria-describedby]="roleGroupDescribedBy()"
    >
      <legend [id]="roleLegendId">
        <span [appTerminologyIdentity]="usage.roleLegend">{{ text.roleLegend() }}</span>{{ ' ' }}<span class="user-create-required" aria-hidden="true">*</span>
      </legend>
      <p class="user-create-hint" [id]="roleHintId" [appTerminologyIdentity]="usage.roleHint">{{ text.roleHint() }}</p>
      <div class="user-create-roles__toolbar">
        <label class="compact-search" [for]="roleFilterId">
          <span class="visually-hidden" [appTerminologyIdentity]="usage.rolesFilter">{{ text.rolesFilter() }}</span>
          <span class="compact-search__icon" aria-hidden="true"></span>
          @if (!roleFilter()) {
            <span class="compact-search__hint" aria-hidden="true" [appTerminologyIdentity]="usage.rolesFilter">{{ text.rolesFilter() }}</span>
          }
          <input
            type="search"
            autocomplete="off"
            data-testid="user-create-role-filter"
            [id]="roleFilterId"
            [value]="roleFilter()"
            (input)="onRoleFilter($event)"
          />
        </label>
        <span data-testid="user-create-role-count">{{ selectedRoleCount() }} <span [appTerminologyIdentity]="usage.selectedOf">{{ text.selectedOf() }}</span> {{ roles().length }} <span [appTerminologyIdentity]="usage.selectedSuffix">{{ text.selectedSuffix() }}</span></span>
      </div>
      <div class="user-create-role-grid" role="group" [attr.aria-labelledby]="roleLegendId">
        @for (role of filteredRoles(); track role.code) {
          <div class="user-create-role" [class.user-create-role--checked]="isRoleChosen(role)">
            <input
              type="checkbox"
              [id]="'user-create-role-' + role.code"
              [attr.data-testid]="'user-create-role-' + role.code"
              [value]="role.code"
              [checked]="isRoleChosen(role)"
              (change)="toggleRole(role, $any($event.target).checked)"
            />
            <label [for]="'user-create-role-' + role.code">{{ role.name }}</label>
            <button
              type="button"
              class="user-create-role__info"
              [pTooltip]="roleTooltip(role)"
              tooltipPosition="top"
              tooltipStyleClass="access-assignment-tooltip"
              [showDelay]="250"
              [appendTo]="'body'"
              [attr.aria-label]="roleDetailsLabel(role)"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.25"></circle>
                <path d="M8 7.1v4M8 4.7h.01"></path>
              </svg>
            </button>
          </div>
        } @empty {
          <p
            class="user-create-role-grid__empty"
            data-testid="user-create-roles-empty"
            [appTerminologyIdentity]="emptyRolesUsage()"
          >{{ emptyRolesText() }}</p>
        }
      </div>
      @if (roleMessage(); as message) {
        <p class="user-create-error" role="alert" [id]="roleErrorId" [attr.data-testid]="roleErrorId">
          {{ message }}
        </p>
      }
    </fieldset>

    <section class="user-create-section" aria-labelledby="user-create-password-title">
      <h4 id="user-create-password-title" [appTerminologyIdentity]="usage.passwordHeading">{{ text.passwordHeading() }}</h4>
      <div class="user-create-fields">
        @for (field of passwordFields(); track field.name) {
          <ng-container *ngTemplateOutlet="textField; context: { $implicit: field }"></ng-container>
        }
      </div>
      <p
        class="user-create-hint"
        data-testid="user-create-password-guidance"
        [appTerminologyIdentity]="usage.passwordGuidance"
      >{{ text.passwordGuidance() }}</p>
    </section>
  </div>
</form>

<ng-template #textField let-field>
  <div class="user-create-field" [class.user-create-field--invalid]="fieldMessage(field)">
    <label [for]="'user-create-' + field.name">
      <span [appTerminologyIdentity]="field.labelUsage">{{ field.label }}</span>
      @if (field.required) {
        {{ ' ' }}<span class="user-create-required" aria-hidden="true">*</span>
      }
    </label>
    <input
      [id]="'user-create-' + field.name"
      [type]="field.type"
      [formControl]="controlFor(field)"
      [autocomplete]="field.autocomplete"
      [attr.maxlength]="maxLengthFor(field)"
      [attr.aria-required]="field.required ? true : null"
      [attr.aria-invalid]="fieldMessage(field) ? true : null"
      [attr.aria-describedby]="describedBy(field)"
    />
    <!-- The message sits directly beneath the field it is about; the standing hint follows it. -->
    @if (fieldMessage(field); as message) {
      <p
        class="user-create-error"
        role="alert"
        [id]="'user-create-error-' + field.name"
        [attr.data-testid]="'user-create-error-' + field.name"
      >
        {{ message }}
      </p>
    }
    <!-- One hint is a sentence; several (the password rules) are a list, under the one id the field is described by. -->
    @if (field.hints.length === 1) {
      <small class="user-create-hint" [id]="'user-create-hint-' + field.name" [appTerminologyIdentity]="field.hints[0].usage">{{ field.hints[0].text }}</small>
    } @else if (field.hints.length > 1) {
      <ul class="user-create-rules" [id]="'user-create-hint-' + field.name" [attr.data-testid]="'user-create-rules-' + field.name">
        @for (hint of field.hints; track hint.usage.token.code) {
          <li [appTerminologyIdentity]="hint.usage">{{ hint.text }}</li>
        }
      </ul>
    }
  </div>
</ng-template>
